/**
 * Configuración de reintentos, backoff y rate limit para el envío de alquileres
 * por WhatsApp. Se usa en el hook de envío, la UI y los tests.
 */
export interface WhatsappRetryConfig {
  /** Número máximo de intentos (1..10). */
  maxAttempts: number;
  /** Retardo base en ms del primer reintento. */
  baseDelayMs: number;
  /** Tope máximo de retardo en ms. */
  maxDelayMs: number;
  /** Factor exponencial. */
  factor: number;
  /** Jitter relativo (0..1) aplicado sobre el retardo calculado. */
  jitterRatio: number;
  /** Límite de envíos por minuto (rate limit del cliente). */
  maxPerMinute: number;
  /** Umbral de alerta: tasa de fallos (%) por encima de la cual se notifica. */
  alertFailureRatePct: number;
  /** Umbral de alerta: latencia p95 (ms) por encima de la cual se notifica. */
  alertP95Ms: number;
  /** Webhook HTTPS al que notificar las alertas (opcional). */
  alertWebhookUrl?: string;
  /** Email al que notificar las alertas (opcional). */
  alertEmail?: string;
}

export const DEFAULT_WHATSAPP_RETRY: WhatsappRetryConfig = {
  maxAttempts: 3,
  baseDelayMs: 500,
  maxDelayMs: 8000,
  factor: 2,
  jitterRatio: 0.2,
  maxPerMinute: 20,
  alertFailureRatePct: 20,
  alertP95Ms: 8000,
  alertWebhookUrl: "",
  alertEmail: "",
};


/** Entornos configurables por separado. */
export type WhatsappEnv = "development" | "staging" | "production";

export const WHATSAPP_ENVS: WhatsappEnv[] = ["development", "staging", "production"];

export type WhatsappEnvConfig = Record<WhatsappEnv, WhatsappRetryConfig>;

export const DEFAULT_WHATSAPP_ENV_CONFIG: WhatsappEnvConfig = {
  development: { ...DEFAULT_WHATSAPP_RETRY, maxAttempts: 2, maxPerMinute: 5 },
  staging: { ...DEFAULT_WHATSAPP_RETRY },
  production: { ...DEFAULT_WHATSAPP_RETRY, maxAttempts: 4, maxPerMinute: 60 },
};

/** Entorno actual detectado en tiempo de ejecución. */
export function currentWhatsappEnv(): WhatsappEnv {
  try {
    const host = typeof window !== "undefined" ? window.location.hostname : "";
    if (!host || host === "localhost" || host.startsWith("127.")) return "development";
    if (host.includes("preview") || host.includes("staging") || host.includes("lovableproject")) return "staging";
    return "production";
  } catch {
    return "production";
  }
}

export function resolveWhatsappRetryConfig(
  partial?: Partial<WhatsappRetryConfig>,
): WhatsappRetryConfig {
  const cfg = { ...DEFAULT_WHATSAPP_RETRY, ...(partial || {}) };
  return {
    maxAttempts: clamp(Math.floor(Number(cfg.maxAttempts) || 1), 1, 10),
    baseDelayMs: clamp(Number(cfg.baseDelayMs) || 0, 0, 60_000),
    maxDelayMs: clamp(Number(cfg.maxDelayMs) || 0, 0, 120_000),
    factor: clamp(Number(cfg.factor) || 1, 1, 10),
    jitterRatio: clamp(Number(cfg.jitterRatio) || 0, 0, 1),
    maxPerMinute: clamp(Math.floor(Number(cfg.maxPerMinute) || 1), 1, 600),
    alertFailureRatePct: clamp(Number(cfg.alertFailureRatePct ?? 20), 0, 100),
    alertP95Ms: clamp(Number(cfg.alertP95Ms ?? 8000), 0, 600_000),
    alertWebhookUrl: String(cfg.alertWebhookUrl ?? "").trim().slice(0, 500),
    alertEmail: String(cfg.alertEmail ?? "").trim().slice(0, 200),
  };
}

/** Evalúa umbrales de alerta contra las métricas observadas. */
export interface WhatsappAlert {
  kind: "failure_rate" | "p95";
  severity: "warning" | "critical";
  value: number;
  threshold: number;
  message: string;
}

export function evaluateWhatsappAlerts(
  metrics: { failureRatePct: number; p95Ms: number; sample: number },
  cfg: WhatsappRetryConfig,
  envLabel = "",
): WhatsappAlert[] {
  const alerts: WhatsappAlert[] = [];
  if (metrics.sample > 0 && metrics.failureRatePct > cfg.alertFailureRatePct) {
    alerts.push({
      kind: "failure_rate",
      severity: metrics.failureRatePct > cfg.alertFailureRatePct * 2 ? "critical" : "warning",
      value: metrics.failureRatePct,
      threshold: cfg.alertFailureRatePct,
      message: `Tasa de fallos ${metrics.failureRatePct.toFixed(1)}% supera el umbral ${cfg.alertFailureRatePct}%${envLabel ? ` (${envLabel})` : ""}`,
    });
  }
  if (metrics.p95Ms > cfg.alertP95Ms) {
    alerts.push({
      kind: "p95",
      severity: metrics.p95Ms > cfg.alertP95Ms * 2 ? "critical" : "warning",
      value: metrics.p95Ms,
      threshold: cfg.alertP95Ms,
      message: `Latencia p95 ${(metrics.p95Ms / 1000).toFixed(2)} s supera el umbral ${(cfg.alertP95Ms / 1000).toFixed(2)} s${envLabel ? ` (${envLabel})` : ""}`,
    });
  }
  return alerts;
}

/** Normaliza la configuración completa por entorno (para la pantalla de ajustes). */
export function resolveWhatsappEnvConfig(partial?: Partial<WhatsappEnvConfig>): WhatsappEnvConfig {
  return WHATSAPP_ENVS.reduce((acc, env) => {
    acc[env] = resolveWhatsappRetryConfig({
      ...DEFAULT_WHATSAPP_ENV_CONFIG[env],
      ...(partial?.[env] || {}),
    });
    return acc;
  }, {} as WhatsappEnvConfig);
}

/** Retardo (ms) antes del reintento nº `attempt` (1-indexado), acotado por maxDelayMs. */
export function computeWhatsappBackoff(
  attempt: number,
  cfg: WhatsappRetryConfig = DEFAULT_WHATSAPP_RETRY,
  random: () => number = Math.random,
): number {
  const n = Math.max(1, Math.floor(attempt));
  const exp = Math.min(cfg.maxDelayMs, cfg.baseDelayMs * Math.pow(cfg.factor, n - 1));
  const jitter = exp * cfg.jitterRatio * random();
  return Math.floor(Math.min(cfg.maxDelayMs, exp + jitter));
}

/** Causas seleccionables al reintentar un envío fallido. */
export const WHATSAPP_RETRY_CAUSES = [
  { value: "wrong_number", label: "Número incorrecto / corregido" },
  { value: "network_error", label: "Error de red o timeout" },
  { value: "provider_error", label: "Error del proveedor (Twilio/Meta)" },
  { value: "rate_limited", label: "Límite de envíos alcanzado" },
  { value: "client_request", label: "Petición del cliente" },
  { value: "other", label: "Otra causa" },
] as const;

export type WhatsappRetryCause = (typeof WHATSAPP_RETRY_CAUSES)[number]["value"];

export function whatsappCauseLabel(cause?: string | null): string {
  return WHATSAPP_RETRY_CAUSES.find((c) => c.value === cause)?.label || cause || "—";
}

/** Rate limiter simple en memoria (ventana deslizante de 1 minuto). */
const sendTimestamps: number[] = [];

export function checkWhatsappRateLimit(
  maxPerMinute: number,
  now: number = Date.now(),
): { allowed: boolean; retryAfterMs: number } {
  const windowMs = 60_000;
  while (sendTimestamps.length && now - sendTimestamps[0] > windowMs) sendTimestamps.shift();
  if (sendTimestamps.length >= Math.max(1, maxPerMinute)) {
    return { allowed: false, retryAfterMs: windowMs - (now - sendTimestamps[0]) };
  }
  sendTimestamps.push(now);
  return { allowed: true, retryAfterMs: 0 };
}

export function resetWhatsappRateLimit() {
  sendTimestamps.length = 0;
}

function clamp(n: number, min: number, max: number) {
  return Math.min(max, Math.max(min, n));
}

/**
 * Clave de idempotencia estable para un envío de WhatsApp.
 * Depende del alquiler, el teléfono destino y el contenido exacto del mensaje,
 * de modo que reintentar el mismo envío no duplica mensajes ni registros.
 */
export function computeWhatsappIdempotencyKey(
  rentalId: string,
  phone: string,
  message: string,
): string {
  const normalizedPhone = String(phone || "").replace(/[^\d+]/g, "");
  const normalizedMsg = String(message || "").replace(/\s+/g, " ").trim();
  return `wa:${rentalId}:${fnv1a(`${normalizedPhone}|${normalizedMsg}`)}`;
}

function fnv1a(input: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(16).padStart(8, "0");
}

/** Campos numéricos auditables de los umbrales/reintentos. */
export const WHATSAPP_AUDITABLE_FIELDS: (keyof WhatsappRetryConfig)[] = [
  "maxAttempts",
  "baseDelayMs",
  "maxDelayMs",
  "factor",
  "jitterRatio",
  "maxPerMinute",
  "alertFailureRatePct",
  "alertP95Ms",
  "alertWebhookUrl",
  "alertEmail",
];

/** Diferencias entre dos configuraciones por entorno (para auditoría). */
export function diffWhatsappConfig(
  before: WhatsappRetryConfig,
  after: WhatsappRetryConfig,
): { changedFields: string[]; oldValues: Record<string, unknown>; newValues: Record<string, unknown> } {
  const changedFields: string[] = [];
  const oldValues: Record<string, unknown> = {};
  const newValues: Record<string, unknown> = {};
  for (const key of WHATSAPP_AUDITABLE_FIELDS) {
    if (before?.[key] !== after?.[key]) {
      changedFields.push(String(key));
      oldValues[String(key)] = before?.[key] ?? null;
      newValues[String(key)] = after?.[key] ?? null;
    }
  }
  return { changedFields, oldValues, newValues };
}
